// src/encounter/intelligence/explain-panel.tsx
//
// The Explain entry in the encounter drawer's "Scrips Intelligence" section: the "Explain a body system" chip
// (Heart & vessels, the one system offered today), the request, and the card. Read-only: it writes nothing to the
// record, and the footer says so ("Nothing is added to the chart until you act.", round 18e C).
//
// Behind VITE_INTELLIGENCE_ENGINE (the drawer decides; env.ts, on in dev-web.yml only). The engine itself refuses
// outside dev, off its allow-list, for a non-clinical role and without consent; this flag is convenience, not the
// control (A8). Loading: Cancel appears after 20 s (A12 state copy).
import { useEffect, useId, useState } from 'react';
import { Button, Skeleton } from '@scripsteam/scrips-signal-ds';
import { ExplainCard } from './explain-card';
import { C } from './explain-card-classes';
import { SESSION_EXPIRED, WORDS } from './explanation';
import { useExplain, type ExplainSystem, type UseExplainOptions } from './use-explain';

/** One Explain request from the rail: a new `seq` asks again (the footer's "Explain a body system" choice). */
export interface ExplainRequest {
  system: ExplainSystem;
  seq: number;
}

/** The A12 state copy: Cancel shows once a request has run this long. */
export const CANCEL_AFTER_MS = 20_000;

export interface ExplainPanelProps {
  patientId: string;
  encounterId?: string | null;
  /** The visit's appointment id, sent beside the encounter id (live 4 Nov: a just-arrived encounter). */
  appointmentId?: string | null;
  /** Test seams, forwarded to useExplain. */
  fetch?: UseExplainOptions['fetch'];
  token?: UseExplainOptions['token'];
  /**
   * Frame v2 rail (round 18r design): the request to run. The panel runs it when it mounts (inside the notice gate, so
   * only after the clinician has confirmed the notice) and again on each new `seq`; it draws no chip row and no footer
   * line (the rail footer holds both). Absent: the drawer's own chip row and footer line.
   */
  request?: ExplainRequest;
  /** Forwarded to the card: "Open the results list". */
  onOpenResults?: () => void;
}

export function ExplainPanel({ patientId, encounterId = null, appointmentId = null, fetch, token, request, onOpenResults }: ExplainPanelProps) {
  const ex = useExplain({ patientId, encounterId, appointmentId, fetch, token });
  const { run, cancel } = ex;
  const system = request?.system;
  const seq = request?.seq;
  useEffect(() => {
    if (system === undefined || seq === undefined) return;
    // Codex (#954): a system picked while another loads replaces it, as in the catalog rail.
    cancel();
    void run(system);
  }, [system, seq, run, cancel]);
  const [slow, setSlow] = useState(false);
  const loading = ex.status === 'loading';
  const labelId = `${useId()}-label`;
  const retry = !loading && ((!!ex.notice && ex.notice !== SESSION_EXPIRED) || (ex.explanation?.state === 'failed' && ex.explanation.retryable));

  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setSlow(true), CANCEL_AFTER_MS);
    return () => {
      clearTimeout(t);
      setSlow(false);
    };
  }, [loading]);

  return (
    <div data-slot="intelligence-explain" className={C.lines}>
      {request ? null : (
        <div className={C.footerRow}>
          <span id={labelId}>Explain a body system</span>
          <Button variant="secondary" size="sm" className="text-xs font-normal" aria-describedby={labelId} onClick={() => void ex.run('heart-vessels')} disabled={loading}>
            Heart &amp; vessels
          </Button>
        </div>
      )}
      {loading ? (
        <div role="status" aria-busy="true" aria-label="Loading" data-slot="intelligence-explain-loading">
          <Skeleton className="h-16" />
          {slow ? (
            <Button variant="tertiary" size="sm" className="text-xs font-normal" onClick={ex.cancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      ) : null}
      {ex.notice ? (
        <p role="status" className={C.stateLine} data-slot="intelligence-explain-notice">
          {ex.notice}
        </p>
      ) : null}
      {ex.explanation ? <ExplainCard explanation={ex.explanation} onOpenResults={onOpenResults} /> : null}
      {retry ? (
        // H11: an engine failure never blocks the visit; the clinician may try again, and the encounter stays usable.
        <Button variant="tertiary" size="sm" className="text-xs font-normal" onClick={() => void ex.run(system ?? 'heart-vessels')}>
          Retry
        </Button>
      ) : null}
      {request ? null : <p className={C.meta}>{WORDS.footer}</p>}
    </div>
  );
}
